import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["src", "alt"]

import { computed } from 'vue';
import DMSuiteIcon from '@/components/iconify/DMSuiteIcon.vue';
import {
  isSidenavImageIcon,
  resolveSidenavIconUrl,
} from '@/utils/sidenavIcons';
import {
  DEFAULT_ICON_COLOR,
  DEFAULT_ICON_SIZE,
  FALLBACK_ICON_NAME,
  type IconGradientConfig,
} from '@/config/dmSuiteIconPalette';

export interface AppIconItemProps {
  icon?: string | null;
  icon_type?: string | null;
  icon_name?: string | null;
  icon_color?: string | null;
  icon_size?: number | null;
  icon_style?: 'solid' | 'gradient' | string | null;
  icon_gradient?: IconGradientConfig | string | null;
  label?: string;
}

interface Props {
  app?: AppIconItemProps | null;
  icon?: string | null;
  iconType?: string | null;
  iconName?: string | null;
  iconColor?: string | null;
  iconSize?: number | null;
  iconStyle?: 'solid' | 'gradient' | string | null;
  iconGradient?: IconGradientConfig | string | null;
  styleType?: 'solid' | 'gradient' | string | null;
  gradient?: IconGradientConfig | string | null;
  size?: number | string | null;
  color?: string | null;
  label?: string;
  customClass?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AppIcon',
  props: {
    app: { default: null },
    icon: { default: null },
    iconType: { default: null },
    iconName: { default: null },
    iconColor: { default: null },
    iconSize: { default: null },
    iconStyle: { default: null },
    iconGradient: { default: null },
    styleType: { default: null },
    gradient: { default: null },
    size: { default: null },
    color: { default: null },
    label: { default: '' },
    customClass: { default: '' }
  },
  setup(__props: any) {

const props = __props;

const resolvedType = computed(() => {
  return props.iconType || props.app?.icon_type || null;
});

const resolvedName = computed(() => {
  return props.iconName || props.app?.icon_name || null;
});

const resolvedColor = computed(() => {
  return props.color || props.iconColor || props.app?.icon_color || DEFAULT_ICON_COLOR;
});

const resolvedStyleType = computed(() => {
  return (
    props.styleType ||
    props.iconStyle ||
    props.app?.icon_style ||
    (resolvedGradient.value ? 'gradient' : 'solid')
  );
});

const resolvedGradient = computed(() => {
  return props.gradient || props.iconGradient || props.app?.icon_gradient || null;
});

const resolvedSize = computed(() => {
  if (props.size !== null && props.size !== undefined) {
    return Number(props.size);
  }
  if (props.iconSize !== null && props.iconSize !== undefined) {
    return Number(props.iconSize);
  }
  if (props.app?.icon_size !== null && props.app?.icon_size !== undefined) {
    return Number(props.app.icon_size);
  }
  return DEFAULT_ICON_SIZE;
});

const computedSize = computed(() => resolvedSize.value);

const rawIcon = computed(() => {
  return props.icon || props.app?.icon || '';
});

const isIconify = computed(() => {
  if (resolvedType.value === 'iconify') return true;
  if (resolvedName.value && resolvedName.value.includes(':')) return true;
  const str = String(rawIcon.value || '').trim();
  if (!str) return false;
  if (isSidenavImageIcon(str)) return false;
  if (str.startsWith('fa') || str.includes(' fa-')) return false;
  return str.includes(':') && !str.includes('/') && !str.includes('.');
});

const iconifyIdentifier = computed(() => {
  if (resolvedName.value) {
    return resolvedName.value;
  }
  const str = String(rawIcon.value || '').trim();
  if (str && str.includes(':') && !str.includes('/')) {
    return str;
  }
  return FALLBACK_ICON_NAME;
});

const isImage = computed(() => {
  if (isIconify.value) return false;
  return isSidenavImageIcon(rawIcon.value);
});

const imageUrl = computed(() => {
  return resolveSidenavIconUrl(rawIcon.value);
});

const effectiveIconString = computed(() => {
  return rawIcon.value;
});

const computedLabel = computed(() => {
  return props.label || props.app?.label || 'App Icon';
});

const wrapperStyle = computed(() => ({
  width: `${computedSize.value}px`,
  height: `${computedSize.value}px`,
}));

const legacyFontStyle = computed(() => {
  const styles: Record<string, string> = {};
  if (resolvedColor.value) {
    styles.color = resolvedColor.value;
  }
  return styles;
});

const imageStyle = computed(() => ({
  width: `${computedSize.value}px`,
  height: `${computedSize.value}px`,
}));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("span", {
    class: "app-icon-wrapper inline-flex shrink-0 items-center justify-center select-none",
    style: _normalizeStyle(wrapperStyle.value)
  }, [
    (isIconify.value)
      ? (_openBlock(), _createBlock(DMSuiteIcon, {
          key: 0,
          icon: iconifyIdentifier.value,
          size: computedSize.value,
          "style-type": resolvedStyleType.value,
          color: resolvedColor.value,
          gradient: resolvedGradient.value,
          "custom-class": __props.customClass
        }, null, 8, ["icon", "size", "style-type", "color", "gradient", "custom-class"]))
      : (isImage.value)
        ? (_openBlock(), _createElementBlock("img", {
            key: 1,
            src: imageUrl.value,
            alt: computedLabel.value,
            style: _normalizeStyle(imageStyle.value),
            class: _normalizeClass(['app-icon-image object-contain rounded', __props.customClass]),
            loading: "lazy"
          }, null, 14, _hoisted_1))
        : (_openBlock(), _createElementBlock("i", {
            key: 2,
            class: _normalizeClass([[effectiveIconString.value || 'fas fa-circle', __props.customClass], "app-icon-font"]),
            style: _normalizeStyle(legacyFontStyle.value)
          }, null, 6))
  ], 4))
}
}

})